// RUNN — granular, region-aware consent manager.
// Source of truth is localStorage for instant UX; when a backend + signed-in
// user exist, every change is also appended to an immutable Firestore ledger
// (consents/) as GDPR/Law25/DPDP evidence. Bump CONSENT_VERSION to re-prompt.
import { backendLive } from '../lib/config.js';
import { currentFramework } from './regions.js';

export const CONSENT_VERSION = '2026-06-21';
const LS_KEY = 'runn.consent.v1';

// essential is always granted; the rest are independently controllable.
export const CATEGORIES = ['essential', 'functional', 'location', 'health', 'analytics', 'marketing'];
const ALWAYS_ON = ['essential'];

export const CATEGORY_COPY = {
  essential: { name: 'Essential', desc: 'Sign-in, security, and core gameplay. Always on.', locked: true },
  functional:{ name: 'Functional', desc: 'Remembers your region, language and preferences.' },
  location:  { name: 'Location', desc: 'Precise GPS to draw, claim and hold real-world turf. Core to the game — but always your explicit choice.' },
  health:    { name: 'Health & fitness', desc: 'Steps, pace, heart rate and intake you choose to share. Sensitive data — never on by default.' },
  analytics: { name: 'Analytics', desc: 'Anonymous, aggregated usage so we can improve RUNN.' },
  marketing: { name: 'Marketing', desc: 'Product news, early-access and offers. Opt-in, opt-out anytime.' },
};

const listeners = new Set();

export function getConsent() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null; // policy changed → re-prompt
    return parsed;
  } catch {
    return null;
  }
}

export function needsConsent() {
  return getConsent() === null;
}

/** Region-aware starting toggles for the preferences UI. */
export function defaultConsent() {
  const fw = currentFramework();
  const optIn = fw.model === 'opt-in';
  return {
    essential: true,
    functional: !optIn,   // off by default in opt-in regions
    location: false,      // always explicit, every region
    health: false,        // always explicit, every region (special category)
    analytics: !optIn,
    marketing: false,     // always explicit (CASL / GDPR / DPDP)
  };
}

export function hasConsent(cat) {
  const c = getConsent();
  return c ? !!c.categories[cat] : ALWAYS_ON.includes(cat);
}

export function setConsent(categories, method = 'preferences') {
  const fw = currentFramework();
  const record = {
    version: CONSENT_VERSION,
    region: fw.region,
    framework: fw.label,
    method,
    categories: { ...categories, essential: true },
    updatedAt: new Date().toISOString(),
  };
  try { localStorage.setItem(LS_KEY, JSON.stringify(record)); } catch { /* noop */ }
  applyConsent(record);
  void recordToLedger(record);
  listeners.forEach((cb) => { try { cb(record); } catch { /* noop */ } });
  return record;
}

export function acceptAll(method = 'accept-all') {
  const c = {};
  CATEGORIES.forEach((k) => (c[k] = true));
  return setConsent(c, method);
}

export function rejectNonEssential(method = 'reject-non-essential') {
  const c = {};
  CATEGORIES.forEach((k) => (c[k] = ALWAYS_ON.includes(k)));
  return setConsent(c, method);
}

export function withdrawAll() {
  return rejectNonEssential('withdraw');
}

export function onConsentChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// Expose granted/denied on <html data-consent-*> so any third-party tag,
// the map's geolocation call, etc. can gate cleanly on the attribute.
function applyConsent(record) {
  const el = document.documentElement;
  CATEGORIES.forEach((k) =>
    el.setAttribute(`data-consent-${k}`, record.categories[k] ? 'granted' : 'denied')
  );
}

async function recordToLedger(record) {
  if (!backendLive) return; // backend not wired yet — localStorage stands in
  try {
    const [{ addDoc, collection, serverTimestamp }, { db, auth }] = await Promise.all([
      import('firebase/firestore'),
      import('../lib/firebase.js'),
    ]);
    const uid = auth().currentUser?.uid;
    if (!uid) return; // ledger rows are bound to an authenticated principal
    await addDoc(collection(db(), 'consents'), {
      uid,
      version: record.version,
      region: record.region,
      framework: record.framework,
      method: record.method,
      categories: record.categories,
      userAgent: navigator.userAgent,
      recordedAt: serverTimestamp(),
    });
  } catch (e) {
    console.warn('[RUNN] consent ledger write skipped', e);
  }
}

/** Re-emit current state on load so attribute hooks are set even before any UI. */
export function primeConsentAttributes() {
  const c = getConsent();
  if (c) applyConsent(c);
}
